/**
 * Audience matcher and variant picker (contract v1). `visitors` is already filtered by the BE;
 * it is re-checked here as defense in depth. `origins` / `paths` are targeting, not security.
 * First match wins; an unknown condition key never matches (forward compatibility).
 */
import type { ContentPathRule, ContentRule, ContentVisitors } from "../widget-config";

export interface MatchContext {
  /** Session kind; `null` before the handshake confirmed anything. */
  visitors: Exclude<ContentVisitors, "ALL"> | null;
  /** Host origin, e.g. `https://acme.com`. */
  origin: string;
  /** Full host URL (only its pathname is matched). */
  url: string;
}

const KNOWN_KEYS = new Set(["visitors", "origins", "paths"]);

export function matchOrigins(rule: readonly string[], origin: string): boolean {
  if (rule.length === 0) return true;
  const o = origin.toLowerCase();
  return rule.some((raw) => {
    const r = raw.toLowerCase();
    const star = "https://*.";
    if (r.startsWith(star)) {
      const suffix = `.${r.slice(star.length)}`;
      return o.startsWith("https://") && o.length > 8 + suffix.length && o.endsWith(suffix);
    }
    return o === r;
  });
}

function safeDecode(v: string): string {
  try {
    return decodeURIComponent(v);
  } catch {
    return v;
  }
}

/** Linear scan (a `/\/+$/` replace is quadratic on a long slash run in a host-controlled path). */
function stripTrailingSlash(p: string): string {
  if (p.length <= 1 || !p.endsWith("/")) return p;
  let end = p.length;
  while (end > 0 && p.charCodeAt(end - 1) === 47) end--;
  return end === 0 ? "/" : p.slice(0, end);
}

function pathnameOf(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split(/[?#]/)[0] || "/";
  }
}

export function matchPath(rule: ContentPathRule, url: string): boolean {
  const path = stripTrailingSlash(safeDecode(pathnameOf(url)).toLowerCase());
  const value = safeDecode(rule.value).toLowerCase();
  switch (rule.match) {
    case "EQUALS":
      return path === stripTrailingSlash(value);
    case "STARTS_WITH":
      return path.startsWith(value);
    case "CONTAINS":
      return path.includes(value);
    default:
      return false;
  }
}

function matchVisitors(want: unknown, have: MatchContext["visitors"]): boolean {
  if (want === undefined || want === "ALL") return true;
  return (want === "ANONYMOUS" || want === "SIGNED_IN") && want === have;
}

export function matchRule(rule: ContentRule | undefined, ctx: MatchContext): boolean {
  if (!rule) return true;
  if (Object.keys(rule).some((k) => !KNOWN_KEYS.has(k))) return false;
  if (!matchVisitors(rule.visitors, ctx.visitors)) return false;
  if (rule.origins !== undefined) {
    if (!Array.isArray(rule.origins) || !matchOrigins(rule.origins, ctx.origin)) return false;
  }
  if (rule.paths !== undefined) {
    if (!Array.isArray(rule.paths)) return false;
    if (rule.paths.length > 0 && !rule.paths.some((p) => matchPath(p, ctx.url))) return false;
  }
  return true;
}

export function pickVariant<V extends { when?: ContentRule }>(
  variants: readonly V[],
  ctx: MatchContext,
): V | null {
  return variants.find((v) => matchRule(v.when, ctx)) ?? null;
}
